import React, { useEffect, useState } from 'react';
import { ArrowDownIcon } from './Icons';

const Hero = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
    >
      {/* Static background image */}
      <div className="absolute inset-0">
        <img
          src="/img/essential-oils-hero.jpg"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      {/* Dark overlay with gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-brand-dark/70 via-brand-dark/50 to-brand-dark/80" />

      {/* Animated particles/dots */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-brand-gold/30 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${4 + Math.random() * 4}s`
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left side - Text Content */}
          <div className="text-center lg:text-left order-1 max-w-2xl mx-auto lg:mx-0 lg:max-w-none">
            {/* Premium badge */}
            <div 
              className={`mb-8 transition-all duration-1000 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <span className="inline-flex items-center gap-3 px-6 py-3 glass rounded-full">
                <span className="w-2 h-2 bg-brand-gold rounded-full animate-pulse" />
                <span className="font-work text-sm text-white/90 tracking-widest uppercase">
                  Massage bei Ihnen zu Hause
                </span>
                <span className="w-2 h-2 bg-brand-gold rounded-full animate-pulse" />
              </span>
            </div>

            {/* Main Headline */}
            <h1 
              data-testid="hero-headline"
              className={`font-marcellus text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-white mb-6 leading-tight transition-all duration-1000 delay-200 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              Body<span className="gradient-text">Harmonic</span>
            </h1>

            {/* Subheadline */}
            <p 
              data-testid="hero-subheadline"
              className={`font-work text-xl md:text-2xl lg:text-3xl text-white/90 mb-12 transition-all duration-1000 delay-400 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              Premium Bodywork & Massage
            </p>

            {/* CTA Buttons */}
            <div 
              className={`flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4 transition-all duration-1000 delay-700 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <a
                href="#buchung"
                data-testid="hero-cta-book"
                onClick={(e) => scrollToSection(e, '#buchung')}
                className="group relative px-10 py-4 bg-brand-gold text-brand-dark font-work font-semibold rounded-full overflow-hidden magnetic-btn"
              >
                <span className="relative z-10">Jetzt buchen</span>
                <div className="absolute inset-0 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </a>
              <a
                href="#bewertungen"
                data-testid="hero-cta-videos"
                onClick={(e) => scrollToSection(e, '#bewertungen')}
                className="px-10 py-4 border-2 border-white/30 text-white font-work font-medium rounded-full hover:border-brand-gold hover:text-brand-gold transition-all duration-300 magnetic-btn backdrop-blur-sm"
              >
                Bewertungen ansehen
              </a>
            </div>
          </div>

          {/* Right side - Video Preview */}
          <div 
            className={`order-2 transition-all duration-1000 delay-500 ${
              isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="flex justify-center lg:justify-center xl:justify-end max-w-sm mx-auto lg:mx-auto xl:mr-0">
              <button
                onClick={() => setIsVideoModalOpen(true)}
                className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[9/16] max-w-[280px] md:max-w-xs group cursor-pointer w-full"
              >
              <img
                src="/img/photo_2025-11-09_17-10-29.jpg"
                alt="BodyHarmonic Video Preview"
                className="w-full h-full object-cover"
              />
              
              {/* Dark overlay */}
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-all duration-300" />
              
              {/* Play button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 bg-white/30 backdrop-blur-sm rounded-full group-hover:bg-brand-gold/80 group-hover:scale-110 transition-all duration-300 flex items-center justify-center shadow-lg">
                  <svg 
                    className="w-8 h-8 text-white ml-1" 
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
              </div>
            </button>
            </div>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {isVideoModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-dark/95 backdrop-blur-md"
          onClick={() => setIsVideoModalOpen(false)}
        >
          <div className="relative w-full max-w-5xl aspect-video animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setIsVideoModalOpen(false)}
              className="absolute -top-12 right-0 text-white/70 hover:text-white font-work text-sm flex items-center gap-2 group"
            >
              Schließen 
              <span className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">×</span>
            </button>
            <iframe
              src="https://www.youtube.com/embed/dEQqnkbc1eU?autoplay=1&rel=0&modestbranding=1"
              title="BodyHarmonic Video"
              className="w-full h-full rounded-2xl shadow-2xl"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <a
          href="#leistungen"
          onClick={(e) => scrollToSection(e, '#leistungen')}
          className="flex flex-col items-center text-white/50 hover:text-brand-gold transition-colors group"
          aria-label="Nach unten scrollen"
        >
          <span className="font-work text-xs tracking-widest uppercase mb-2">Entdecken</span>
          <ArrowDownIcon className="w-5 h-5 animate-bounce" />
        </a>
      </div>
    </section>
  );
};

export default Hero;
